import { useEffect, useState, useRef } from "react";
import Users from "./Users";
import Roles from "./Roles";
import { departmentAPI, positionAPI, officeAPI, userAPI } from "../services/api";
import { StatusBadge, Modal, FormField, Btn, Th, Td, ActionBtns, ThActions, ConfirmDialog, Toast, Can, errMessage, TableScroll, Pagination } from "../components/shared/index";
import { useLanguage } from "../context/LanguageContext";
import { MdSearch, MdAccountBalance, MdAssignment, MdBarChart, MdNotes, MdAccessTime, MdEdit, MdWork, MdDelete, MdBadge, MdLabel, MdCheckCircle, MdApartment } from "react-icons/md";

const BROWN  = "var(--brown)";
const BORDER = "var(--border)";
const BG     = "var(--cream)";
const MUTED  = "var(--muted)";
const KH     = "'Khmer OS Siemreap','Noto Sans Khmer',sans-serif";
const MO     = "'Moul','Noto Sans Khmer',serif";

function SkeletonRow({ cols }) {
  return (
    <tr>
      {Array(cols).fill(0).map((_,i) => (
        <td key={i} style={{ padding:"12px 16px", borderBottom:`1px solid ${BORDER}` }}>
          <div style={{ height:14, background:"var(--border)", borderRadius:4 }} />
        </td>
      ))}
    </tr>
  );
}

function SectionLabel({ children }) {
  return (
    <p style={{ fontSize:11, color:MUTED, fontWeight:600,
                marginBottom:8, marginTop:14,
                borderBottom:`1px solid ${BORDER}`,
                paddingBottom:4, fontFamily:KH }}>
      {children}
    </p>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div style={{ display:"flex", alignItems:"flex-start", gap:10,
                  background:BG, padding:"9px 14px",
                  borderRadius:8, marginBottom:6 }}>
      <span style={{ flexShrink:0, marginTop:2, color:MUTED, display:"flex", alignItems:"center" }}>{icon}</span>
      <div>
        <p style={{ fontSize:10, color:MUTED, marginBottom:2, fontFamily:KH }}>{label}</p>
        <div style={{ fontSize:13, fontWeight:500, color:"var(--text)", fontFamily:KH }}>
          {value || "—"}
        </div>
      </div>
    </div>
  );
}

export function Departments() {
  const { t } = useLanguage();
  const [list,       setList]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("");
  const [modal,      setModal]      = useState(null);
  const [form,       setForm]       = useState({});
  const [selected,   setSelected]   = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [toast,      setToast]      = useState(null);
  const [err,        setErr]      = useState(null);
  const [page,       setPage]       = useState(0);
  const [pageSize,   setPageSize]   = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await departmentAPI.getAll(filter || undefined, search || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : data?.content || []);
    } catch (e) {
      console.error("Load departments:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        departmentCode: form.departmentCode || "",
        departmentName: form.departmentName || "",
        description:    form.description    || "",
        status:         form.status         || "ACTIVE",
      };
      if (editId) {
        await departmentAPI.update(editId, payload);
      } else {
        await departmentAPI.create(payload);
      }
      await load();
      setToast(editId ? "កែប្រែនាយកដ្ឋានបានជោគជ័យ!" : "បន្ថែមនាយកដ្ឋានបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await departmentAPI.delete(id);
      await load();
      if ((selected?.departmentId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបនាយកដ្ឋានបានជោគជ័យ!"));
    } catch (e) {
      setErr(errMessage(e));
    }
  };

  const openEdit = (d) => {
    setEditId(d.departmentId ?? d.id);
    setForm({
      departmentCode: d.departmentCode,
      departmentName: d.departmentName,
      description:    d.description,
      status:         d.status,
    });
    setModal("add");
  };

  useEffect(() => { setPage(0); }, [search, filter]);

  const filtered = list.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [d.departmentCode, d.departmentName, d.description]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ fontSize:18, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:3 }}>
            {t("នាយកដ្ឋាន")}
          </h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("គ្រប់គ្រងនាយកដ្ឋានទាំងអស់ក្នុងអង្គភាព")}
          </p>
        </div>
        <Can perm="department" action="create">
        <button onClick={() => { setEditId(null); setForm({ status:"ACTIVE" }); setModal("add"); }}
          style={{ padding:"10px 20px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, fontFamily:KH }}>
          {t("+ បន្ថែមនាយកដ្ឋាន")}
        </button>
        </Can>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"នាយកដ្ឋានសរុប",     value:list.length,                                          color:BROWN },
          { label:"ដំណើរការ",            value:list.filter(d=>d.status==="ACTIVE").length,           color:"#10B981" },
          { label:"មិនដំណើរការ",         value:list.filter(d=>d.status==="INACTIVE").length,         color:"#EF4444" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"16px 20px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:28, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder={t("ស្វែងរកនាយកដ្ឋាន....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`,
                   borderRadius:8, fontSize:13, outline:"none",
                   background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="ACTIVE">{t("ដំណើរការ")}</option>
          <option value="INACTIVE">{t("មិនដំណើរការ")}</option>
        </select>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខ")}</Th>
              <Th>{t("កូដ")}</Th>
              <Th>{t("ឈ្មោះនាយកដ្ឋាន")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <Th>{t("ការពិពណ៌នា")}</Th>
              <ThActions perm="department" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={6} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មាននាយកដ្ឋាន")}
                </td>
              </tr>
            ) : pageItems.map(d => (
              <tr key={d.departmentId ?? d.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{d.departmentId ?? d.id}</Td>
                <Td muted>{d.departmentCode || "—"}</Td>
                <Td>{d.departmentName || d.name || "—"}</Td>
                <Td>
                  <StatusBadge status={d.status === "ACTIVE" ? "ដំណើរការ" : "មិនដំណើរការ"} />
                </Td>
                <Td muted>{d.description || "—"}</Td>
                <ActionBtns perm="department"
                  onView={() => { setSelected(d); setModal("view"); }}
                  onEdit={() => openEdit(d)}
                  onDelete={() => handleDelete(d.departmentId ?? d.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបនាយកដ្ឋាននេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          title={editId ? "កែប្រែនាយកដ្ឋាន" : "បន្ថែមនាយកដ្ឋានថ្មី"}
          subtitle={t("បំពេញព័ត៌មានខាងក្រោម")}
          key={editId || "new-dept"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); }}
        >
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FormField label={t("កូដនាយកដ្ឋាន")}
              value={form.departmentCode||""}
              onChange={v => f("departmentCode", v)}
              placeholder={t("ឧ. IT, HR, FIN...")} />
            <FormField label={t("ស្ថានភាព")}
              value={form.status||"ACTIVE"}
              onChange={v => f("status", v)}
              options={["ACTIVE","INACTIVE"]} />
          </div>
          <FormField label={t("ឈ្មោះនាយកដ្ឋាន")}
            value={form.departmentName||""}
            onChange={v => f("departmentName", v)}
            placeholder={t("ឧ. នាយកដ្ឋានព័ត៌មានវិទ្យា...")} />
          <FormField label={t("ការពិពណ៌នា")}
            value={form.description||""}
            onChange={v => f("description", v)}
            rows={3}
            placeholder={t("ពិពណ៌នាអំពីតួនាទី និងមុខងារ...")} />

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.departmentName || "នាយកដ្ឋាន"}
          subtitle={selected.departmentCode || ""}
          onClose={() => setModal(null)}
        >
          <SectionLabel>{t("ព័ត៌មានសង្ខេប")}</SectionLabel>
          <InfoRow icon={<MdAccountBalance size={16}/>} label={t("លេខសម្គាល់")}      value={selected.departmentId ?? selected.id} />
          <InfoRow icon={<MdBadge size={16}/>}            label={t("កូដនាយកដ្ឋាន")}   value={selected.departmentCode} />
          <InfoRow icon={<MdAssignment size={16}/>}     label={t("ឈ្មោះនាយកដ្ឋាន")} value={selected.departmentName} />
          <InfoRow icon={<MdBarChart size={16}/>}       label={t("ស្ថានភាព")}
            value={
              <StatusBadge
                status={selected.status === "ACTIVE" ? "ដំណើរការ" : "មិនដំណើរការ"} />
            } />

          {selected.description && (
            <>
              <SectionLabel>{t("ការពិពណ៌នា")}</SectionLabel>
              <InfoRow icon={<MdNotes size={16}/>} label={t("ពិពណ៌នា")} value={selected.description} />
            </>
          )}

          <SectionLabel>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccessTime size={16}/>} label={t("ថ្ងៃបង្កើត")}
              value={selected.createdAt?.slice(0,10)} />
            <InfoRow icon={<MdEdit size={16}/>}       label={t("ថ្ងៃកែប្រែ")}
              value={selected.updatedAt?.slice(0,10)} />
          </div>

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between" }}>
            <Can perm="department" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="department" action="delete">
              <Btn variant="danger"
                onClick={() => handleDelete(selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Department managers live in their own sub-resource on the API
 * (GET/POST/DELETE /departments/{id}/managers), so they get their own page
 * rather than hiding inside the department modal.
 */
/**
 * Type-to-filter picker. Lists can run to hundreds of departments/users, so a
 * plain <select> stops being usable — this filters as you type.
 * options: [{ value, label, sub }]
 */
function SearchSelect({ value, onChange, options, placeholder, emptyText = "រកមិនឃើញ" }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [q,    setQ]    = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setQ("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = options.find(o => String(o.value) === String(value));
  const needle   = q.trim().toLowerCase();
  const shown    = needle
    ? options.filter(o =>
        `${o.label} ${o.sub || ""}`.toLowerCase().includes(needle))
    : options;

  const boxStyle = {
    width:"100%", padding:"9px 12px", border:`1px solid ${BORDER}`,
    borderRadius:8, fontSize:13, outline:"none", background:"var(--surface)",
    fontFamily:KH, boxSizing:"border-box",
  };

  return (
    <div ref={wrapRef} style={{ position:"relative", width:"100%" }}>
      {open ? (
        <div style={{ ...boxStyle, display:"flex", alignItems:"center", gap:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder={t("វាយដើម្បីស្វែងរក...")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          style={{ ...boxStyle, textAlign:"left", cursor:"pointer",
                   display:"flex", alignItems:"center", gap:8,
                   color: selected ? "var(--text)" : MUTED }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis",
                         whiteSpace:"nowrap" }}>
            {selected ? selected.label : placeholder}
          </span>
        </button>
      )}

      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0,
                      maxHeight:260, overflowY:"auto", background:"var(--surface)",
                      border:`1px solid ${BORDER}`, borderRadius:8, zIndex:50,
                      boxShadow:"0 12px 32px rgba(92,45,14,0.16)" }}>
          {shown.length === 0 ? (
            <p style={{ padding:"14px 12px", margin:0, fontSize:12,
                        color:MUTED, fontFamily:KH, textAlign:"center" }}>
              {t(emptyText)}
            </p>
          ) : shown.map(o => (
            <button key={o.value} type="button"
              onClick={() => { onChange(String(o.value)); setOpen(false); setQ(""); }}
              style={{ display:"block", width:"100%", textAlign:"left",
                       padding:"9px 12px", border:"none", cursor:"pointer",
                       background: String(o.value) === String(value) ? BG : "var(--surface)",
                       borderBottom:`1px solid ${BORDER}` }}
              onMouseEnter={e => e.currentTarget.style.background = BG}
              onMouseLeave={e => e.currentTarget.style.background =
                String(o.value) === String(value) ? BG : "var(--surface)"}>
              <span style={{ display:"block", fontSize:13, color:"var(--text)", fontFamily:KH }}>
                {o.label}
              </span>
              {o.sub && (
                <span style={{ display:"block", fontSize:11, color:MUTED,
                               fontFamily:KH, marginTop:2 }}>
                  {o.sub}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DepartmentManagers() {
  const { t } = useLanguage();
  const [depts,        setDepts]        = useState([]);
  const [users,        setUsers]        = useState([]);
  const [deptId,       setDeptId]       = useState("");
  const [managers,     setManagers]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [busy,         setBusy]         = useState(false);
  const [newMgrId,     setNewMgrId]     = useState("");
  const [newMgrPrimary,setNewMgrPrimary]= useState(false);
  const [addOpen,      setAddOpen]      = useState(false);
  const [userQuery,    setUserQuery]    = useState("");
  const [removeUserId, setRemoveUserId] = useState(null);
  const [toast,        setToast]        = useState(null);
  const [err,          setErr]          = useState(null);

  useEffect(() => {
    departmentAPI.getAll()
      .then(res => {
        const d = res?.data?.data ?? res?.data;
        setDepts(Array.isArray(d) ? d : d?.content || []);
      })
      .catch(e => setErr(errMessage(e)));

    // The picker searches client-side, so it needs every user — not just page 0.
    (async () => {
      try {
        const SIZE = 200;
        const res  = await userAPI.getAll(0, SIZE);
        const d    = res?.data?.data ?? res?.data;
        let all    = d?.content || (Array.isArray(d) ? d : []);
        const totalPages = d?.totalPages || 1;
        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => userAPI.getAll(i + 1, SIZE))
          );
          rest.forEach(r => {
            const rd = r?.data?.data ?? r?.data;
            all = all.concat(rd?.content || []);
          });
        }
        setUsers(all);
      } catch (e) {
        console.warn("Load users:", e.message);
      }
    })();
  }, []);

  const userLabel = (u) =>
    u?.userNameKh || u?.userNameEn || u?.email || `ID: ${u?.userId ?? u?.id}`;

  const loadManagers = async (id) => {
    if (!id) { setManagers([]); return; }
    setLoading(true);
    try {
      const res = await departmentAPI.getManagers(id);
      const d   = res?.data?.data ?? res?.data;
      setManagers(Array.isArray(d) ? d : d?.content || []);
    } catch (e) {
      setErr(errMessage(e));
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const selectDept = (id) => {
    setDeptId(id);
    setNewMgrId(""); setNewMgrPrimary(false);
    loadManagers(id);
  };

  const openAdd = () => {
    setNewMgrId(""); setNewMgrPrimary(false); setUserQuery("");
    setAddOpen(true);
  };
  const closeAdd = () => {
    setAddOpen(false); setNewMgrId(""); setNewMgrPrimary(false); setUserQuery("");
  };

  // users not already managing this department, narrowed by the modal's search
  const pickableUsers = users
    .filter(u => !managers.some(m => m.userId === (u.userId ?? u.id)))
    .filter(u => {
      const q = userQuery.trim().toLowerCase();
      if (!q) return true;
      return [u.userNameKh, u.userNameEn, u.email,
              u.roleName || u.role?.displayName]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });

  const handleAdd = async () => {
    if (!deptId || !newMgrId) return;
    setBusy(true);
    try {
      await departmentAPI.addManager(deptId, parseInt(newMgrId, 10), newMgrPrimary);
      await loadManagers(deptId);
      closeAdd();
      setToast(t("បន្ថែមអ្នកគ្រប់គ្រងបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    const userId = removeUserId;
    setRemoveUserId(null);
    if (!deptId || !userId) return;
    setBusy(true);
    try {
      await departmentAPI.removeManager(deptId, userId);
      await loadManagers(deptId);
      setToast(t("ដកអ្នកគ្រប់គ្រងបានជោគជ័យ!"));
    } catch (e) { setErr(errMessage(e)); }
    finally { setBusy(false); }
  };

  const selectedDept = depts.find(d => String(d.departmentId ?? d.id) === String(deptId));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h3 style={{ fontSize:18, fontWeight:700, color:BROWN,
                     fontFamily:MO, marginBottom:3 }}>
          {t("អ្នកគ្រប់គ្រងនាយកដ្ឋាន")}
        </h3>
        <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
          {t("កំណត់អ្នកប្រើប្រាស់ជាអ្នកគ្រប់គ្រងសម្រាប់នាយកដ្ឋាននីមួយៗ")}
        </p>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, padding:20, marginBottom:20 }}>
        <p style={{ fontSize:12, fontWeight:600, color:MUTED,
                    marginBottom:8, fontFamily:KH }}>
          {t("ជ្រើសរើសនាយកដ្ឋាន")}
        </p>
        <SearchSelect
          value={deptId}
          onChange={selectDept}
          placeholder={t("ជ្រើសរើសនាយកដ្ឋាន...")}
          emptyText="រកមិនឃើញនាយកដ្ឋាន"
          options={depts.map(d => ({
            value: d.departmentId ?? d.id,
            label: d.departmentName || d.name || `ID: ${d.departmentId ?? d.id}`,
            sub:   d.departmentCode || "",
          }))} />
      </div>

      {!deptId ? (
        <div style={{ background:"var(--surface)", borderRadius:10, border:`1px solid ${BORDER}`,
                      padding:"48px 20px", textAlign:"center" }}>
          <MdAccountBalance size={40} style={{ color:BORDER }} />
          <p style={{ fontSize:13, color:MUTED, fontFamily:KH, marginTop:10 }}>
            {t("សូមជ្រើសរើសនាយកដ្ឋានជាមុនសិន ដើម្បីមើលអ្នកគ្រប់គ្រង")}
          </p>
        </div>
      ) : (
        <div style={{ background:"var(--surface)", borderRadius:10,
                      border:`1px solid ${BORDER}` }}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${BORDER}`,
                        display:"flex", justifyContent:"space-between",
                        alignItems:"center", flexWrap:"wrap", gap:8 }}>
            <p style={{ fontSize:14, fontWeight:700, color:BROWN,
                        fontFamily:MO, margin:0 }}>
              {selectedDept?.departmentName || "នាយកដ្ឋាន"}
            </p>
            <span style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
              អ្នកគ្រប់គ្រង {managers.length} នាក់
            </span>
          </div>

          <TableScroll>
            <thead>
              <tr>
                <Th>{t("ឈ្មោះ")}</Th>
                <Th>{t("អ៊ីមែល")}</Th>
                <Th>{t("តួនាទី")}</Th>
                <ThActions perm="department" actions={["delete"]} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [1,2,3].map(i => <SkeletonRow key={i} cols={4} />)
              ) : managers.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding:"32px", textAlign:"center",
                                            color:MUTED, fontSize:13, fontFamily:KH }}>
                    {t("គ្មានអ្នកគ្រប់គ្រងសម្រាប់នាយកដ្ឋាននេះ")}
                  </td>
                </tr>
              ) : managers.map(m => (
                <tr key={m.departmentManagerId ?? m.userId}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                  <Td>
                    {m.userNameKh || `ID: ${m.userId}`}
                    {m.isPrimary && (
                      <span style={{ marginLeft:8, padding:"2px 9px", borderRadius:20,
                                     fontSize:10, fontWeight:600, background:"#D1FAE5",
                                     color:"#065F46", fontFamily:KH, whiteSpace:"nowrap" }}>
                        {t("ចម្បង")}
                      </span>
                    )}
                  </Td>
                  <Td muted>{m.userEmail || "—"}</Td>
                  <Td muted>{m.roleName  || "—"}</Td>
                  <ActionBtns perm="department"
                    onDelete={() => setRemoveUserId(m.userId)} />
                </tr>
              ))}
            </tbody>
          </TableScroll>

          <Can perm="department" action="edit">
            <div style={{ padding:"14px 20px", borderTop:`1px solid ${BORDER}`,
                          background:BG }}>
              <button onClick={openAdd}
                style={{ padding:"9px 18px", background:BROWN, color:"#fff",
                         border:"none", borderRadius:8, cursor:"pointer",
                         fontSize:13, fontWeight:500, fontFamily:KH }}>
                {t("+ បន្ថែមអ្នកគ្រប់គ្រង")}
              </button>
            </div>
          </Can>
        </div>
      )}

      {addOpen && (
        <Modal
          title={t("បន្ថែមអ្នកគ្រប់គ្រង")}
          subtitle={selectedDept?.departmentName || ""}
          onClose={closeAdd}
        >
          <div style={{ display:"flex", alignItems:"center", gap:8,
                        border:`1px solid ${BORDER}`, borderRadius:8,
                        padding:"0 12px", marginBottom:12 }}>
            <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
            <input autoFocus value={userQuery}
              onChange={e => setUserQuery(e.target.value)}
              placeholder={t("ស្វែងរកតាមឈ្មោះ តួនាទី ឬអ៊ីមែល...")}
              style={{ border:"none", outline:"none", background:"transparent",
                       flex:1, padding:"10px 0", fontSize:13, fontFamily:KH }} />
          </div>

          <div style={{ maxHeight:300, overflowY:"auto",
                        border:`1px solid ${BORDER}`, borderRadius:8 }}>
            {pickableUsers.length === 0 ? (
              <p style={{ padding:"28px 16px", margin:0, textAlign:"center",
                          fontSize:13, color:MUTED, fontFamily:KH }}>
                {t("រកមិនឃើញអ្នកប្រើប្រាស់")}
              </p>
            ) : pickableUsers.map(u => {
              const uid      = u.userId ?? u.id;
              const isPicked = String(uid) === String(newMgrId);
              return (
                <button key={uid} type="button"
                  onClick={() => setNewMgrId(String(uid))}
                  style={{ display:"flex", alignItems:"center", gap:10,
                           width:"100%", textAlign:"left", padding:"10px 14px",
                           border:"none", borderBottom:`1px solid ${BORDER}`,
                           cursor:"pointer", background: isPicked ? BG : "var(--surface)" }}
                  onMouseEnter={e => e.currentTarget.style.background = BG}
                  onMouseLeave={e => e.currentTarget.style.background = isPicked ? BG : "var(--surface)"}>
                  <span style={{ flex:1, minWidth:0 }}>
                    <span style={{ display:"block", fontSize:13, fontWeight:500,
                                   color:"var(--text)", fontFamily:KH }}>
                      {userLabel(u)}
                    </span>
                    <span style={{ display:"block", fontSize:11, color:MUTED,
                                   fontFamily:KH, marginTop:2 }}>
                      {[u.roleName || u.role?.displayName, u.email]
                        .filter(Boolean).join(" · ") || "—"}
                    </span>
                  </span>
                  {isPicked && <MdCheckCircle size={18} style={{ color:"#10B981", flexShrink:0 }} />}
                </button>
              );
            })}
          </div>

          <label style={{ display:"flex", alignItems:"center", gap:6,
                          fontSize:12, color:MUTED, fontFamily:KH,
                          marginTop:14, cursor:"pointer" }}>
            <input type="checkbox" checked={newMgrPrimary}
              onChange={e => setNewMgrPrimary(e.target.checked)} />
            {t("ធ្វើជាអ្នកគ្រប់គ្រងចម្បង")}
          </label>

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline" onClick={closeAdd}>{t("បោះបង់")}</Btn>
            <Btn onClick={handleAdd} style={{ opacity:(!newMgrId || busy) ? 0.5 : 1 }}>
              {busy ? "កំពុងរក្សាទុក..." : "បន្ថែម"}
            </Btn>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={removeUserId !== null}
        message={t("តើអ្នកពិតជាចង់ដកអ្នកគ្រប់គ្រងនេះចេញមែនទេ?")}
        onConfirm={doRemove}
        onCancel={() => setRemoveUserId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />
    </div>
  );
}

export function Positions() {
  const { t } = useLanguage();
  const [list,     setList]     = useState([]);
  const [depts,    setDepts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState({});
  const [selected, setSelected] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast,    setToast]    = useState(null);
  const [err,      setErr]    = useState(null);
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await positionAPI.getAll(search || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : data?.content || []);
    } catch (e) {
      console.error("Load positions:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadDepts = async () => {
    try {
      const res  = await departmentAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setDepts(Array.isArray(data) ? data : data?.content || []);
    } catch (e) {
      console.error("Load departments:", e);
    }
  };

  useEffect(() => { load(); loadDepts(); }, []);

  const deptName = (id) => {
    const d = depts.find(x => (x.departmentId ?? x.id) === id);
    return d?.departmentName || d?.name || "—";
  };
  const deptOptions = depts.map(d => ({
    value: String(d.departmentId ?? d.id),
    label: d.departmentName || d.name,
  }));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        departmentId:   form.departmentId ? Number(form.departmentId) : null,
        positionCode:   form.positionCode   || "",
        positionName:   form.positionName   || "",
        positionNameEn: form.positionNameEn || "",
        description:    form.description     || "",
      };
      if (editId) {
        await positionAPI.update(editId, payload);
      } else {
        await positionAPI.create(payload);
      }
      await load();
      setToast(editId ? "កែប្រែតំណែងបានជោគជ័យ!" : "បន្ថែមតំណែងបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await positionAPI.delete(id);
      await load();
      if ((selected?.positionId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបតំណែងបានជោគជ័យ!"));
    } catch (e) {
      setErr(errMessage(e));
    }
  };

  const openEdit = (p) => {
    setEditId(p.positionId ?? p.id);
    setForm({
      departmentId:   (p.departmentId ?? p.department?.departmentId ?? "") === "" ? "" : String(p.departmentId ?? p.department?.departmentId ?? ""),
      positionCode:   p.positionCode,
      positionName:   p.positionName,
      positionNameEn: p.positionNameEn,
      description:    p.description,
    });
    setModal("add");
  };

  const filtered = list.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [p.positionCode, p.positionName, p.positionNameEn]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  useEffect(() => { setPage(0); }, [search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ fontSize:18, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:3 }}>
            {t("តំណែង")}
          </h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("គ្រប់គ្រងតំណែងទាំងអស់ក្នុងអង្គភាព")}
          </p>
        </div>
        <Can perm="position" action="create">
        <button onClick={() => { setEditId(null); setForm({}); setModal("add"); }}
          style={{ padding:"10px 20px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, fontFamily:KH }}>
          {t("+ បន្ថែមតំណែង")}
        </button>
        </Can>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"តំណែងសរុប", value:list.length,   color:BROWN },
          { label:"ប្រើប្រាស់", value:list.length,   color:"#10B981" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"16px 20px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:28, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder={t("ស្វែងរកតំណែង....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <button onClick={load}
          style={{ padding:"9px 16px", border:`1px solid ${BORDER}`,
                   borderRadius:8, background:"var(--surface)", cursor:"pointer",
                   fontSize:13, fontFamily:KH }}>
          <MdSearch size={15} style={{marginRight:4}}/> {t("ស្វែងរក")}
        </button>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខ")}</Th>
              <Th>{t("កូដ")}</Th>
              <Th>{t("ឈ្មោះតំណែង")}</Th>
              <Th>{t("ឈ្មោះ (EN)")}</Th>
              <Th>{t("នាយកដ្ឋាន")}</Th>
              <Th>{t("ថ្ងៃបង្កើត")}</Th>
              <ThActions perm="position" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={7} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានតំណែង")}
                </td>
              </tr>
            ) : pageItems.map(p => (
              <tr key={p.positionId ?? p.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{p.positionId ?? p.id}</Td>
                <Td muted>{p.positionCode || "—"}</Td>
                <Td>{p.positionName || p.name || "—"}</Td>
                <Td muted>{p.positionNameEn || "—"}</Td>
                <Td muted>{p.departmentName || p.department?.departmentName || deptName(p.departmentId ?? p.department?.departmentId) || "—"}</Td>
                <Td muted>{p.createdAt?.slice(0,10) || "—"}</Td>
                <ActionBtns perm="position"
                  onView={() => { setSelected(p); setModal("view"); }}
                  onEdit={() => openEdit(p)}
                  onDelete={() => handleDelete(p.positionId ?? p.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបតំណែងនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          title={editId ? "កែប្រែតំណែង" : "បន្ថែមតំណែងថ្មី"}
          subtitle={t("បំពេញព័ត៌មានខាងក្រោម")}
          key={editId || "new-pos"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); }}
        >
          <FormField label={t("នាយកដ្ឋាន")}
            value={form.departmentId||""}
            onChange={v => f("departmentId", v)}
            options={deptOptions} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FormField label={t("កូដតំណែង")}
              value={form.positionCode||""}
              onChange={v => f("positionCode", v)}
              placeholder={t("ឧ. DIR, MGR, SEC...")} />
            <FormField label={t("ឈ្មោះតំណែង")}
              value={form.positionName||""}
              onChange={v => f("positionName", v)}
              placeholder={t("ឧ. អគ្គនាយក, ប្រធាន...")} />
          </div>
          <FormField label={t("ឈ្មោះតំណែង (ភាសាអង់គ្លេស)")}
            value={form.positionNameEn||""}
            onChange={v => f("positionNameEn", v)}
            placeholder="e.g. Director, Manager, Secretary..." />
          <FormField label={t("ការពិពណ៌នា")}
            value={form.description||""}
            onChange={v => f("description", v)}
            rows={3}
            placeholder={t("ពិពណ៌នាអំពីតួនាទី និងមុខងារ...")} />

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.positionName || "តំណែង"}
          subtitle={selected.positionCode || ""}
          onClose={() => setModal(null)}
        >
          <SectionLabel>{t("ព័ត៌មានតំណែង")}</SectionLabel>
          <InfoRow icon={<MdLabel size={16}/>}  label={t("លេខសម្គាល់")}  value={selected.positionId ?? selected.id} />
          <InfoRow icon={<MdBadge size={16}/>} label={t("កូដតំណែង")}    value={selected.positionCode} />
          <InfoRow icon={<MdWork size={16}/>}  label={t("ឈ្មោះតំណែង")}  value={selected.positionName} />
          <InfoRow icon={<MdWork size={16}/>}  label={t("ឈ្មោះ (EN)")}   value={selected.positionNameEn} />
          <InfoRow icon={<MdAccountBalance size={16}/>} label={t("នាយកដ្ឋាន")}
            value={selected.departmentName || selected.department?.departmentName || deptName(selected.departmentId ?? selected.department?.departmentId)} />

          {selected.description && (
            <>
              <SectionLabel>{t("ការពិពណ៌នា")}</SectionLabel>
              <InfoRow icon={<MdNotes size={16}/>} label={t("ពិពណ៌នា")} value={selected.description} />
            </>
          )}

          <SectionLabel>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccessTime size={16}/>} label={t("ថ្ងៃបង្កើត")}
              value={selected.createdAt?.slice(0,10)} />
            <InfoRow icon={<MdEdit size={16}/>}       label={t("ថ្ងៃកែប្រែ")}
              value={selected.updatedAt?.slice(0,10)} />
          </div>

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between" }}>
            <Can perm="position" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="position" action="delete">
              <Btn variant="danger"
                onClick={() => handleDelete(selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Offices() {
  const { t } = useLanguage();
  const [list,     setList]     = useState([]);
  const [depts,    setDepts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [filter,   setFilter]   = useState("");
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState({});
  const [selected, setSelected] = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast,    setToast]    = useState(null);
  const [err,      setErr]    = useState(null);
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const res  = await officeAPI.getAll(filter || undefined, search || undefined);
      const data = res?.data?.data ?? res?.data;
      setList(Array.isArray(data) ? data : data?.content || []);
    } catch (e) {
      console.error("Load offices:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadDepts = async () => {
    try {
      const res  = await departmentAPI.getAll();
      const data = res?.data?.data ?? res?.data;
      setDepts(Array.isArray(data) ? data : data?.content || []);
    } catch (e) {
      console.error("Load departments:", e);
    }
  };

  useEffect(() => { load(); loadDepts(); }, [filter]);

  const deptName = (id) => {
    const d = depts.find(x => (x.departmentId ?? x.id) === id);
    return d?.departmentName || d?.name || "—";
  };
  const deptOptions = depts.map(d => ({
    value: String(d.departmentId ?? d.id),
    label: d.departmentName || d.name,
  }));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        departmentId: form.departmentId ? Number(form.departmentId) : null,
        officeCode:   form.officeCode   || "",
        officeName:   form.officeName   || "",
        status:       form.status       || "ACTIVE",
      };
      if (editId) {
        await officeAPI.update(editId, payload);
      } else {
        await officeAPI.create(payload);
      }
      await load();
      setToast(editId ? "កែប្រែការិយាល័យបានជោគជ័យ!" : "បន្ថែមការិយាល័យបានជោគជ័យ!");
      setModal(null); setForm({}); setEditId(null);
    } catch (e) {
      setErr(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => setDeleteId(id);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await officeAPI.delete(id);
      await load();
      if ((selected?.officeId ?? selected?.id) === id) setModal(null);
      setToast(t("លុបការិយាល័យបានជោគជ័យ!"));
    } catch (e) {
      setErr(errMessage(e));
    }
  };

  const openEdit = (o) => {
    setEditId(o.officeId ?? o.id);
    setForm({
      departmentId: (o.departmentId ?? o.department?.departmentId ?? "") === "" ? "" : String(o.departmentId ?? o.department?.departmentId ?? ""),
      officeCode:   o.officeCode,
      officeName:   o.officeName,
      status:       o.status,
    });
    setModal("add");
  };

  const filtered = list.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [o.officeCode, o.officeName]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  useEffect(() => { setPage(0); }, [search, filter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"center", marginBottom:20 }}>
        <div>
          <h3 style={{ fontSize:18, fontWeight:700, color:BROWN,
                       fontFamily:MO, marginBottom:3 }}>
            {t("ការិយាល័យ")}
          </h3>
          <p style={{ fontSize:12, color:MUTED, fontFamily:KH }}>
            {t("គ្រប់គ្រងការិយាល័យទាំងអស់ក្នុងនាយកដ្ឋាន")}
          </p>
        </div>
        <Can perm="office" action="create">
        <button onClick={() => { setEditId(null); setForm({ status:"ACTIVE" }); setModal("add"); }}
          style={{ padding:"10px 20px", background:BROWN, color:"#fff",
                   border:"none", borderRadius:8, cursor:"pointer",
                   fontSize:13, fontWeight:500, fontFamily:KH }}>
          {t("+ បន្ថែមការិយាល័យ")}
        </button>
        </Can>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)",
                    gap:12, marginBottom:20 }}>
        {[
          { label:"ការិយាល័យសរុប", value:list.length,                                        color:BROWN },
          { label:"ដំណើរការ",        value:list.filter(o=>o.status==="ACTIVE").length,         color:"#10B981" },
          { label:"មិនដំណើរការ",     value:list.filter(o=>o.status==="INACTIVE").length,       color:"#EF4444" },
        ].map((s,i) => (
          <div key={i} style={{ background:"var(--surface)", borderRadius:10,
                                 padding:"16px 20px", border:`1px solid ${BORDER}` }}>
            <p style={{ margin:"0 0 6px", fontSize:12, color:MUTED, fontFamily:KH }}>{s.label}</p>
            <p style={{ margin:0, fontSize:28, fontWeight:700,
                        color:s.color, fontFamily:MO }}>
              {loading ? "..." : s.value}
            </p>
          </div>
        ))}
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, alignItems:"center" }}>
        <div style={{ flex:1, display:"flex", alignItems:"center", gap:8,
                      background:"var(--surface)", border:`1px solid ${BORDER}`,
                      borderRadius:8, padding:"0 12px" }}>
          <MdSearch size={16} style={{ color:MUTED, flexShrink:0 }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder={t("ស្វែងរកការិយាល័យ....")}
            style={{ border:"none", outline:"none", background:"transparent",
                     flex:1, padding:"9px 0", fontSize:13, fontFamily:KH }} />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding:"9px 14px", border:`1px solid ${BORDER}`,
                   borderRadius:8, fontSize:13, outline:"none",
                   background:"var(--surface)", fontFamily:KH }}>
          <option value="">{t("ស្ថានភាពទាំងអស់")}</option>
          <option value="ACTIVE">{t("ដំណើរការ")}</option>
          <option value="INACTIVE">{t("មិនដំណើរការ")}</option>
        </select>
      </div>

      <div style={{ background:"var(--surface)", borderRadius:10,
                    border:`1px solid ${BORDER}`, overflow:"hidden" }}>
        <TableScroll>
          <thead>
            <tr>
              <Th>{t("លេខ")}</Th>
              <Th>{t("កូដ")}</Th>
              <Th>{t("ឈ្មោះការិយាល័យ")}</Th>
              <Th>{t("នាយកដ្ឋាន")}</Th>
              <Th>{t("ស្ថានភាព")}</Th>
              <ThActions perm="office" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4].map(i => <SkeletonRow key={i} cols={6} />)
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding:"32px", textAlign:"center",
                                          color:MUTED, fontSize:13, fontFamily:KH }}>
                  {t("គ្មានការិយាល័យ")}
                </td>
              </tr>
            ) : pageItems.map(o => (
              <tr key={o.officeId ?? o.id}
                onMouseEnter={e => e.currentTarget.style.background = BG}
                onMouseLeave={e => e.currentTarget.style.background = "var(--surface)"}>
                <Td muted>{o.officeId ?? o.id}</Td>
                <Td muted>{o.officeCode || "—"}</Td>
                <Td>{o.officeName || o.name || "—"}</Td>
                <Td muted>{o.departmentName || o.department?.departmentName || deptName(o.departmentId ?? o.department?.departmentId) || "—"}</Td>
                <Td>
                  <StatusBadge status={o.status === "ACTIVE" ? "ដំណើរការ" : "មិនដំណើរការ"} />
                </Td>
                <ActionBtns perm="office"
                  onView={() => { setSelected(o); setModal("view"); }}
                  onEdit={() => openEdit(o)}
                  onDelete={() => handleDelete(o.officeId ?? o.id)}
                />
              </tr>
            ))}
          </tbody>
        </TableScroll>
        <Pagination
          page={page} totalPages={pageCount} totalItems={filtered.length} pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={n => { setPageSize(n); setPage(0); }}
        />
      </div>

      <ConfirmDialog
        open={deleteId !== null}
        message={t("តើអ្នកពិតជាចង់លុបការិយាល័យនេះមែនទេ?")}
        onConfirm={doDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Toast message={toast} onHide={() => setToast(null)} />
      <Toast message={err} type="error" onHide={() => setErr(null)} />

      {modal === "add" && (
        <Modal
          title={editId ? "កែប្រែការិយាល័យ" : "បន្ថែមការិយាល័យថ្មី"}
          subtitle={t("បំពេញព័ត៌មានខាងក្រោម")}
          key={editId || "new-office"}
          onClose={() => { setModal(null); setForm({}); setEditId(null); }}
        >
          <FormField label={t("នាយកដ្ឋាន")}
            value={form.departmentId||""}
            onChange={v => f("departmentId", v)}
            options={deptOptions} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
            <FormField label={t("កូដការិយាល័យ")}
              value={form.officeCode||""}
              onChange={v => f("officeCode", v)}
              placeholder={t("ឧ. OFC-IT...")} />
            <FormField label={t("ស្ថានភាព")}
              value={form.status||"ACTIVE"}
              onChange={v => f("status", v)}
              options={["ACTIVE","INACTIVE"]} />
          </div>
          <FormField label={t("ឈ្មោះការិយាល័យ")}
            value={form.officeName||""}
            onChange={v => f("officeName", v)}
            placeholder={t("ឧ. ការិយាល័យបច្ចេកវិទ្យា...")} />

          <div style={{ display:"flex", justifyContent:"flex-end",
                        gap:10, marginTop:20 }}>
            <Btn variant="outline"
              onClick={() => { setModal(null); setForm({}); setEditId(null); }}>
              {t("បោះបង់")}
            </Btn>
            <Btn onClick={handleSave} style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? "កំពុងរក្សាទុក..." : "រក្សាទុក"}
            </Btn>
          </div>
        </Modal>
      )}

      {modal === "view" && selected && (
        <Modal
          title={selected.officeName || "ការិយាល័យ"}
          subtitle={selected.officeCode || ""}
          onClose={() => setModal(null)}
        >
          <SectionLabel>{t("ព័ត៌មានការិយាល័យ")}</SectionLabel>
          <InfoRow icon={<MdLabel size={16}/>}  label={t("លេខសម្គាល់")}     value={selected.officeId ?? selected.id} />
          <InfoRow icon={<MdBadge size={16}/>} label={t("កូដការិយាល័យ")}   value={selected.officeCode} />
          <InfoRow icon={<MdApartment size={16}/>}  label={t("ឈ្មោះការិយាល័យ")} value={selected.officeName} />
          <InfoRow icon={<MdAccountBalance size={16}/>} label={t("នាយកដ្ឋាន")}
            value={selected.departmentName || selected.department?.departmentName || deptName(selected.departmentId ?? selected.department?.departmentId)} />
          <InfoRow icon={<MdBarChart size={16}/>}       label={t("ស្ថានភាព")}
            value={
              <StatusBadge
                status={selected.status === "ACTIVE" ? "ដំណើរការ" : "មិនដំណើរការ"} />
            } />

          <SectionLabel>{t("ព័ត៌មានប្រព័ន្ធ")}</SectionLabel>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 8px" }}>
            <InfoRow icon={<MdAccessTime size={16}/>} label={t("ថ្ងៃបង្កើត")}
              value={selected.createdAt?.slice(0,10)} />
            <InfoRow icon={<MdEdit size={16}/>}       label={t("ថ្ងៃកែប្រែ")}
              value={selected.updatedAt?.slice(0,10)} />
          </div>

          <div style={{ marginTop:20, display:"flex",
                        justifyContent:"space-between" }}>
            <Can perm="office" action="edit">
              <Btn variant="outline" onClick={() => openEdit(selected)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdEdit size={14}/> {t("កែប្រែ")}
              </Btn>
            </Can>
            <Can perm="office" action="delete">
              <Btn variant="danger"
                onClick={() => handleDelete(selected.officeId ?? selected.id)}
                style={{display:"flex",alignItems:"center",gap:5}}>
                <MdDelete size={14}/> {t("លុប")}
              </Btn>
            </Can>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function Settings({ defaultTab = "departments" }) {
  if (defaultTab === "dept-managers") return <DepartmentManagers />;
  if (defaultTab === "offices")   return <Offices />;
  if (defaultTab === "positions") return <Positions />;
  if (defaultTab === "users")     return <Users />;
  if (defaultTab === "roles")     return <Roles />;
  return <Departments />;
}